import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, UserX } from "lucide-react";
import { toast } from "sonner";

// Follow-ups due today/overdue whose counselor is on leave — shown to the team lead
export default function LeaveFollowups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [team, setTeam] = useState([]);
  const [to, setTo] = useState("");
  const load = () => api.get("/team/leave-followups").then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setTeam(data.filter((u) => u.active !== false && ["counselor", "senior_counselor", "telecaller", "team_lead"].includes(u.role) && (user.role !== "team_lead" || u.team_lead_id === user.id || u.id === user.id)))).catch(() => {});
  }, []); // eslint-disable-line
  if (!d || !d.followups.length) return null;
  const take = async () => {
    try { const { data } = await api.post("/team/followups/take", { ids: d.followups.map((f) => f.id), to: to || user.id }); toast.success(`${data.reassigned} follow-up(s) reassigned`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border-l-4 border-amber-400 rounded-xl p-5 shadow-sm mt-6" data-testid="leave-followups">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-heading font-bold text-[#312E81] flex items-center gap-2"><UserX className="w-4 h-4 text-amber-500" /> Follow-ups of counselors on leave today</h3>
        <span className="text-xs text-slate-500">On leave: {d.on_leave.join(", ")}</span>
        <div className="ml-auto flex items-center gap-2">
          <Select value={to || user.id} onValueChange={setTo}><SelectTrigger className="h-8 w-48" data-testid="leave-fu-to"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{[{ id: user.id, name: `Me (${user.name})` }, ...team.filter((u) => u.id !== user.id && !d.on_leave.includes(u.name))].map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-600" onClick={take} data-testid="leave-fu-take">Reassign all {d.followups.length}</Button>
        </div>
      </div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {d.followups.map((f) => (
          <button key={f.id} onClick={() => f.student_id && navigate(`/students/${f.student_id}`)} className="w-full text-left py-2 flex items-center gap-3 hover:bg-amber-50/50 px-1" data-testid={`leave-fu-${f.id}`}>
            <CalendarClock className="w-4 h-4 text-amber-500 shrink-0" />
            <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-slate-800 truncate">{f.student_name || f.title || "Follow-up"}</div><div className="text-[11px] text-slate-500">{f.type} · {f.notes || f.title || ""}</div></div>
            <div className="text-right text-[11px]"><div className="font-semibold text-rose-600">{f.owner_name}</div>{f.cover_name && <div className="text-emerald-600 font-semibold">covered by {f.cover_name}</div>}<div className="text-slate-400">{String(f.due_at || "").slice(0, 16).replace("T", " ")}</div></div>
          </button>
        ))}
      </div>
    </div>
  );
}
